"use client";

import { ChevronRight, type LucideIcon } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon: LucideIcon;
    isActive?: boolean;
    disabled?: boolean;
    items?: {
      title: string;
      url: string;
      disabled?: boolean;
    }[];
  }[];
}) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Platform</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible key={item.title} asChild defaultOpen={item.isActive}>
            <SidebarMenuItem>
              {/* Main Menu Item */}
              <SidebarMenuButton
                asChild={!item.disabled}
                disabled={item.disabled}
                className={cn(
                  item.disabled &&
                    "opacity-50 cursor-not-allowed pointer-events-none"
                )}
              >
                {item.disabled ? (
                  <span className="flex items-center gap-2 w-full">
                    <item.icon className="size-4" />
                    <span>{item.title}</span>
                  </span>
                ) : (
                  <Link href={`/${item.url}`}>
                    <item.icon className="size-4" />
                    <span>{item.title}</span>
                  </Link>
                )}
              </SidebarMenuButton>

              {/* Collapsible Trigger for items with subitems */}
              {item.items?.length ? (
                <>
                  <CollapsibleTrigger asChild disabled={item.disabled}>
                    <SidebarMenuAction
                      className={cn(
                        "data-[state=open]:rotate-90",
                        item.disabled && "opacity-50 cursor-not-allowed"
                      )}
                      disabled={item.disabled}
                    >
                      <ChevronRight className="size-4" />
                      <span className="sr-only">Toggle</span>
                    </SidebarMenuAction>
                  </CollapsibleTrigger>

                  {/* Subitems */}
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items?.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton
                            asChild={!subItem.disabled}
                            className={cn(
                              subItem.disabled &&
                                "opacity-50 cursor-not-allowed pointer-events-none"
                            )}
                          >
                            {subItem.disabled ? (
                              <span className="flex items-center w-full">
                                <span>{subItem.title}</span>
                              </span>
                            ) : (
                              <Link href={`/${subItem.url}`}>
                                <span>{subItem.title}</span>
                              </Link>
                            )}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </>
              ) : null}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
